import { useState, useEffect } from 'react'
import { supabase } from '../lib/supabaseClient'
import { mutate } from '../lib/dbMutate'
import { Plus, ChevronRight, ChevronDown, X, Check, Users, Radio, Tag } from 'lucide-react'

const buildTree = (nodes, parentId = null) =>
  nodes
    .filter(n => n.parent_id === parentId)
    .sort((a, b) => a.rank_order - b.rank_order)
    .map(n => ({ ...n, children: buildTree(nodes, n.id) }))

const TYPE_COLOR = {
  role: 'text-amber-400',
  unit: 'text-blue-400',
  element: 'text-green-400',
}

export default function EventMCS({ event, players, isGm, orgId, viewerFactionId = null }) {
  const [nodes, setNodes] = useState([])
  const [templates, setTemplates] = useState([])
  const [loading, setLoading] = useState(true)
  const [expanded, setExpanded] = useState({})
  const [managingMembers, setManagingMembers] = useState(null)  // node_id whose member panel is open
  const [editingFreq, setEditingFreq] = useState(null)           // node_id whose freq input is open
  const [freqDraft, setFreqDraft] = useState('')
  const [editingUnit, setEditingUnit] = useState(null)           // node_id whose Event Unit input is open
  const [unitDraft, setUnitDraft] = useState('')
  const [members, setMembers] = useState([])                      // mcs_node_members rows for this event
  const [showDeploy, setShowDeploy] = useState(false)
  const [selectedTemplate, setSelectedTemplate] = useState('')
  const [deploying, setDeploying] = useState(false)
  const [message, setMessage] = useState('')

  useEffect(() => {
    fetchNodes()
    fetchMembers()
    if (orgId) fetchTemplates()
  }, [event.id])

  const fetchNodes = async () => {
    setLoading(true)
    const { data } = await supabase
      .from('mcs_nodes')
      .select('*')
      .eq('event_id', event.id)
      .order('rank_order')
    setNodes(data || [])
    setLoading(false)
  }

  const fetchMembers = async () => {
    // Pull every mcs_node_members row for this event's nodes. Two-step query
    // because mcs_node_members doesn't carry event_id directly.
    const { data: eventNodes } = await supabase
      .from('mcs_nodes').select('id').eq('event_id', event.id)
    if (!eventNodes?.length) { setMembers([]); return }
    const { data } = await supabase
      .from('mcs_node_members')
      .select('id, node_id, user_id, role, user:users!mcs_node_members_user_id_fkey(id, callsign, display_name, email)')
      .in('node_id', eventNodes.map(n => n.id))
    setMembers(data || [])
  }

  // A "faction commander" is the topmost faction-scoped node in its branch:
  // its parent has no faction or a different faction. Adding a player to one
  // auto-grants co-GM authority over that faction; removing the last such
  // assignment reverts them back to player.
  // FIX A — only the literal ROOT of the MCS tree auto-grants the
  // co_gm event_role when a member is added. The prior case-2
  // (faction sub-root) was promoting Cadre / Section Lead / any
  // role-type node that started a new faction branch — but those
  // are chain-of-command roles, not admin tier. node_type has NO
  // bearing on admin access; event_role alone gates it.
  const isFactionCommanderNode = (nodeId, allNodes) => {
    const n = allNodes.find(x => x.id === nodeId)
    if (!n || !n.faction_id) return null
    if (!n.parent_id) return n.faction_id
    return null
  }

  const addMember = async (nodeId, userId) => {
    // Track 1.2 + 5.2 — mutate() wraps the insert so the cache flush
    // happens on success and a friendly error string lands on result
    // on failure. Duplicates aren't surfaced (re-adding is a no-op).
    const memberResult = await mutate('mcs_node_members', () =>
      supabase.from('mcs_node_members').insert({ node_id: nodeId, user_id: userId, role: 'member' })
    )
    if (memberResult.error && !/duplicate/i.test(memberResult.error.message)) {
      setMessage(memberResult.friendlyMessage || 'Add failed.')
      return
    }
    // Auto-grant co-GM if the assigned node is a faction commander. The
    // faction-lock trigger may have just back-filled node.faction_id, so
    // refresh nodes before evaluating.
    const { data: refreshed } = await supabase
      .from('mcs_nodes').select('*').eq('event_id', event.id)
    const factionId = isFactionCommanderNode(nodeId, refreshed || [])
    if (factionId) {
      const coGmResult = await mutate('event_players', () =>
        supabase.from('event_players')
          .update({ event_role: 'co_gm', co_gm_faction_id: factionId })
          .eq('event_id', event.id).eq('user_id', userId)
          .neq('event_role', 'gm')  // never demote a full GM via auto-grant
      )
      if (coGmResult.error) {
        // Silent failure previously — surface so the GM knows the member
        // landed but the co-GM auto-grant didn't.
        setMessage(coGmResult.friendlyMessage || 'Member added; co-GM auto-grant failed.')
      }
    }
    fetchMembers()
    fetchNodes()
  }

  const removeMember = async (memberId) => {
    // Capture the row first — we need user_id + node_id to decide whether
    // the user loses co-GM authority on removal.
    const removed = members.find(m => m.id === memberId)
    await supabase.from('mcs_node_members').delete().eq('id', memberId)

    if (removed) {
      const factionId = isFactionCommanderNode(removed.node_id, nodes)
      if (factionId) {
        // Are they still a member of any OTHER faction-commander node? If
        // so, keep co-GM with that faction; otherwise revert to player.
        const remainingCommander = members
          .filter(m => m.id !== memberId && m.user_id === removed.user_id)
          .map(m => isFactionCommanderNode(m.node_id, nodes))
          .find(Boolean)
        const { data: epRow } = await supabase
          .from('event_players')
          .select('event_role, co_gm_faction_id')
          .eq('event_id', event.id).eq('user_id', removed.user_id).limit(1)
        const role = epRow?.[0]?.event_role
        // Only flip if they're currently a co_gm scoped to the faction we're
        // pulling them out of — leave full GMs and unrelated co-GMs alone.
        if (role === 'co_gm' && epRow?.[0]?.co_gm_faction_id === factionId) {
          if (remainingCommander) {
            await supabase.from('event_players')
              .update({ co_gm_faction_id: remainingCommander })
              .eq('event_id', event.id).eq('user_id', removed.user_id)
          } else {
            await supabase.from('event_players')
              .update({ event_role: 'player', co_gm_faction_id: null })
              .eq('event_id', event.id).eq('user_id', removed.user_id)
          }
        }
      }
    }
    fetchMembers()
  }

  const saveFreq = async (nodeId) => {
    await supabase.from('mcs_nodes').update({ radio_freq: freqDraft.trim() || null }).eq('id', nodeId)
    setEditingFreq(null)
    setFreqDraft('')
    fetchNodes()
  }

  // Event Unit = node.callsign (the field is reused; the label changed
  // because operators kept reading "Callsign" as their personal callsign).
  const saveUnit = async (nodeId) => {
    await supabase.from('mcs_nodes').update({ callsign: unitDraft.trim() || null }).eq('id', nodeId)
    setEditingUnit(null)
    setUnitDraft('')
    fetchNodes()
  }

  const fetchTemplates = async () => {
    const { data } = await supabase
      .from('mcs_templates')
      .select('*, mcs_template_nodes(*)')
      .eq('org_id', orgId)
    setTemplates(data || [])
  }

  const deployTemplate = async () => {
    if (!selectedTemplate) return
    const template = templates.find(t => t.id === selectedTemplate)
    if (!template) return
    setDeploying(true)

    const templateNodes = template.mcs_template_nodes || []
    const idMap = {}
    const ordered = [...templateNodes].sort((a, b) => {
      if (!a.parent_id) return -1
      if (!b.parent_id) return 1
      return 0
    })

    for (const tn of ordered) {
      const { data } = await supabase.from('mcs_nodes').insert({
        event_id: event.id,
        name: tn.name,
        node_type: tn.node_type || 'role',
        parent_id: tn.parent_id ? idMap[tn.parent_id] : null,
        rank_order: tn.rank_order,
        faction_id: null,
      }).select().limit(1)
      if (data?.[0]) idMap[tn.id] = data[0].id
    }

    setShowDeploy(false)
    setSelectedTemplate('')
    setMessage('Template deployed')
    fetchNodes()
    setDeploying(false)
  }

  const clearMCS = async () => {
    if (!window.confirm('Clear the entire command structure for this event?')) return
    await supabase.from('mcs_nodes').delete().eq('event_id', event.id)
    setNodes([])
    setMessage('')
  }

  const toggleExpand = (id) => setExpanded(e => ({ ...e, [id]: !e[id] }))

  // Some event_player rows can survive after their underlying user gets
  // deleted (FK is ON DELETE SET NULL on user_id). Drop those orphans
  // up-front so the picker never tries to render an unknown name.
  const assignablePlayers = (players || []).filter(p =>
    p && p.user_id && p.users &&
    ['player', 'delegate', 'co_gm'].includes(p.event_role)
  )

  const getPlayerName = (player) =>
    player?.callsign || player?.display_name || player?.email || 'Unknown'

  // Set of user_ids that are already a member of SOME node in this event.
  // Used to enforce the per-event uniqueness rule (Bug 3) — players in this
  // set can't be added to another node in the same event.
  const eventMemberUserIds = new Set(members.map(m => m.user_id))

  const renderNode = (node, depth = 0) => {
    const hasChildren = node.children?.length > 0
    const isExpanded = expanded[node.id] !== false
    const isManagingMembers = managingMembers === node.id
    const isEditingFreq = editingFreq === node.id
    const typeColor = TYPE_COLOR[node.node_type] || 'text-gray-400'
    const nodeMembers = members.filter(m => m.node_id === node.id)
    const memberUserIds = new Set(nodeMembers.map(m => m.user_id))
    // Tree-view rule: 0 members → "Unassigned" hint, 1 member → name inline,
    // 2+ → just the count. Individual names for multi-person nodes live in
    // the manage-members panel, not the tree.
    const soloMember = nodeMembers.length === 1 ? nodeMembers[0] : null

    // Candidate filter for the manage-members picker.
    // - Drop players already on this node.
    // - Drop players already on another node in this event (per-event unique).
    // - Drop players with no event_players.faction_id — MCS slotting now
    //   requires a faction first (the GM must assign a faction in the
    //   PLAYERS tab before they can be added to a node).
    // - Drop players whose faction doesn't match a faction-scoped node.
    const eligiblePlayers = assignablePlayers.filter(p => {
      if (memberUserIds.has(p.user_id)) return false
      if (eventMemberUserIds.has(p.user_id)) return false
      if (!p.faction_id) return false
      if (node.faction_id && p.faction_id !== node.faction_id) return false
      return true
    })
    // Surface unfactioned players separately so the GM understands WHY they
    // don't appear in the candidate list — counts only those still without a
    // faction (excludes players already slotted on another node).
    const unfactionedCount = assignablePlayers.filter(
      p => !p.faction_id && !eventMemberUserIds.has(p.user_id) && !memberUserIds.has(p.user_id)
    ).length

    return (
      <div key={node.id}>
        <div
          className="flex items-center gap-2 py-2 px-3 rounded-lg hover:bg-gray-700 group"
          style={{ paddingLeft: `${12 + depth * 22}px` }}
        >
          <button onClick={() => toggleExpand(node.id)} className="w-4 flex-shrink-0 text-gray-500">
            {hasChildren
              ? (isExpanded ? <ChevronDown size={12} /> : <ChevronRight size={12} />)
              : <span className="w-3 inline-block border-l border-gray-700 h-3 ml-1" />
            }
          </button>

          <div className="flex items-center gap-2 flex-1 min-w-0">
            {/* SEC 1 — R/U/E badge. Tooltip surfaces the full
                type name so the single-letter abbreviation isn't
                cryptic. Default flipped to 'U' to match the new
                schema default ('unit' is now the execution layer
                default rather than 'role'). */}
            <span className={`text-xs font-mono ${typeColor} flex-shrink-0`} title={node.node_type || 'unit'}>
              {node.node_type?.[0]?.toUpperCase() || 'U'}
            </span>
            <span className="text-sm font-medium text-gray-200 truncate">{node.name}</span>
            {node.callsign && <span className="text-xs text-cyan-300 flex-shrink-0">[{node.callsign}]</span>}
            {node.radio_freq && (
              <span className="text-xs text-blue-300 flex-shrink-0 flex items-center gap-1">
                <Radio size={10} /> {node.radio_freq}
              </span>
            )}

            {/* Membership indicator — 0/1/2+ rule */}
            {soloMember ? (
              <div className="flex items-center gap-1.5 ml-auto flex-shrink-0">
                <div className="w-5 h-5 rounded-full bg-amber-500 flex items-center justify-center text-gray-900 font-bold text-xs">
                  {getPlayerName(soloMember.user)[0].toUpperCase()}
                </div>
                <span className="text-xs text-amber-300">{getPlayerName(soloMember.user)}</span>
              </div>
            ) : nodeMembers.length >= 2 ? (
              <span className="ml-auto flex-shrink-0 text-xs text-gray-400 flex items-center gap-1">
                <Users size={11} /> {nodeMembers.length} members
              </span>
            ) : (
              <span className="ml-auto flex-shrink-0 text-xs text-gray-600 hidden group-hover:block">Unassigned</span>
            )}

            {isGm && (
              <div className="flex items-center gap-1 flex-shrink-0">
                <button
                  onClick={() => { setManagingMembers(isManagingMembers ? null : node.id); setEditingFreq(null); setEditingUnit(null) }}
                  className="hidden group-hover:flex items-center gap-1 text-xs text-gray-500 hover:text-blue-400"
                  title="Manage members"
                >
                  <Users size={11} />
                </button>
                <button
                  onClick={() => { setEditingUnit(editingUnit === node.id ? null : node.id); setUnitDraft(node.callsign || ''); setEditingFreq(null); setManagingMembers(null) }}
                  className="hidden group-hover:flex items-center gap-1 text-xs text-gray-500 hover:text-amber-400"
                  title="Set Event Unit"
                >
                  <Tag size={11} />
                </button>
                <button
                  onClick={() => { setEditingFreq(isEditingFreq ? null : node.id); setFreqDraft(node.radio_freq || ''); setManagingMembers(null); setEditingUnit(null) }}
                  className="hidden group-hover:flex items-center gap-1 text-xs text-gray-500 hover:text-blue-400"
                  title="Set radio frequency"
                >
                  <Radio size={11} />
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Event Unit editor — sets node.callsign. Renamed in the UI from
            "Callsign" to "Event Unit" so operators stop confusing the node
            tag (e.g. "ROMEO 2") with their personal callsign. */}
        {editingUnit === node.id && isGm && (
          <div
            className="mx-3 mb-1 bg-gray-700 rounded-lg border border-gray-600 px-3 py-2 flex items-center gap-2"
            style={{ marginLeft: `${12 + depth * 22 + 24}px` }}
          >
            <Tag size={12} className="text-amber-400" />
            <input
              type="text"
              value={unitDraft}
              onChange={e => setUnitDraft(e.target.value)}
              placeholder="e.g. ROMEO 2"
              maxLength={32}
              className="flex-1 bg-gray-800 text-amber-300 text-sm px-2 py-1 rounded border border-gray-600 focus:outline-none focus:border-amber-500"
            />
            <button onClick={() => saveUnit(node.id)} className="text-xs bg-amber-700 hover:bg-amber-600 text-white px-2 py-1 rounded">Save</button>
            <button onClick={() => setEditingUnit(null)} className="text-xs text-gray-400">Cancel</button>
          </div>
        )}

        {/* Radio frequency editor */}
        {isEditingFreq && isGm && (
          <div
            className="mx-3 mb-1 bg-gray-700 rounded-lg border border-gray-600 px-3 py-2 flex items-center gap-2"
            style={{ marginLeft: `${12 + depth * 22 + 24}px` }}
          >
            <Radio size={12} className="text-blue-400" />
            <input
              type="text"
              value={freqDraft}
              onChange={e => setFreqDraft(e.target.value)}
              placeholder="e.g. 462.5625 MHz"
              maxLength={32}
              className="flex-1 bg-gray-800 text-blue-300 text-sm px-2 py-1 rounded border border-gray-600 focus:outline-none focus:border-blue-500"
            />
            <button onClick={() => saveFreq(node.id)} className="text-xs bg-blue-700 hover:bg-blue-600 text-white px-2 py-1 rounded">Save</button>
            <button onClick={() => setEditingFreq(null)} className="text-xs text-gray-400">Cancel</button>
          </div>
        )}

        {/* Member management */}
        {isManagingMembers && isGm && (
          <div
            className="mx-3 mb-1 bg-gray-700 rounded-lg border border-gray-600 overflow-hidden"
            style={{ marginLeft: `${12 + depth * 22 + 24}px` }}
          >
            <div className="px-3 py-2 border-b border-gray-600 flex items-center justify-between">
              <p className="text-xs text-gray-400">Members of {node.name}</p>
              <button onClick={() => setManagingMembers(null)} className="text-xs text-gray-500 hover:text-gray-300">Close</button>
            </div>
            {nodeMembers.length > 0 && (
              <div className="px-3 py-2 border-b border-gray-600 flex flex-wrap gap-1">
                {nodeMembers.map(m => (
                  <span key={m.id} className="inline-flex items-center gap-1 text-xs bg-gray-800 text-gray-200 px-2 py-1 rounded">
                    {m.user?.callsign || m.user?.display_name || m.user?.email || 'unknown'}
                    <button onClick={() => removeMember(m.id)} className="text-gray-500 hover:text-red-400" title="Remove"><X size={10} /></button>
                  </span>
                ))}
              </div>
            )}
            <div className="max-h-40 overflow-y-auto">
              {eligiblePlayers.length === 0 ? (
                <div className="px-3 py-2 text-xs text-gray-500 space-y-1">
                  <p>
                    No eligible players. Candidates must not already be on another
                    node in this event{node.faction_id ? ' and must match this node’s faction' : ''}.
                  </p>
                  {unfactionedCount > 0 && (
                    <p className="text-amber-400">
                      {unfactionedCount} player{unfactionedCount === 1 ? '' : 's'} without a faction. Assign to a faction first in the PLAYERS tab.
                    </p>
                  )}
                </div>
              ) : (
                eligiblePlayers.map(p => {
                  const name = p.users?.callsign || p.users?.display_name || p.users?.email
                  return (
                    <button
                      key={p.user_id}
                      onClick={() => addMember(node.id, p.user_id)}
                      className="w-full text-left px-3 py-2 text-sm text-gray-300 hover:bg-gray-600 flex items-center gap-2"
                    >
                      <Plus size={11} className="text-blue-400" />
                      <span>{name}</span>
                    </button>
                  )
                })
              )}
            </div>
          </div>
        )}

        {hasChildren && isExpanded && node.children.map(child => renderNode(child, depth + 1))}
      </div>
    )
  }

  // For co-GMs, narrow the tree to nodes their faction owns. Unscoped nodes
  // (no faction_id) are still visible because they're "shared" command tiers
  // — they're the connective tissue between faction branches.
  const scopedNodes = viewerFactionId
    ? nodes.filter(n => !n.faction_id || n.faction_id === viewerFactionId)
    : nodes
  const tree = buildTree(scopedNodes)
  // A node is "staffed" once it has at least one member. Single-leader
  // assignment was retired in favour of the manage-members flow.
  const staffedNodeIds = new Set(members.map(m => m.node_id))
  const assignedCount = nodes.filter(n => staffedNodeIds.has(n.id)).length

  if (loading) return <p className="text-gray-500 text-sm mt-4">Loading command structure...</p>

  return (
    <div className="mt-2">
      {message && (
        <div className="mb-3 p-2 rounded bg-green-900 text-green-300 text-sm">{message}</div>
      )}

      {nodes.length === 0 ? (
        <div className="text-center py-12 border border-dashed border-gray-700 rounded-xl">
          <p className="text-gray-500 text-sm mb-4">No command structure deployed for this event</p>
          {isGm && (
            <div className="flex flex-col items-center gap-3">
              {templates.length > 0 && (
                <button
                  onClick={() => setShowDeploy(!showDeploy)}
                  className="flex items-center gap-2 bg-amber-500 hover:bg-amber-400 text-gray-900 font-bold text-sm px-4 py-2 rounded-lg"
                >
                  Deploy Template
                </button>
              )}
              {showDeploy && (
                <div className="mt-2 flex items-center gap-2">
                  <select
                    value={selectedTemplate}
                    onChange={e => setSelectedTemplate(e.target.value)}
                    className="bg-gray-700 text-white rounded-lg px-3 py-2 text-sm focus:outline-none"
                  >
                    <option value="">Select template...</option>
                    {templates.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                  </select>
                  <button
                    onClick={deployTemplate}
                    disabled={!selectedTemplate || deploying}
                    className="flex items-center gap-1 text-sm bg-green-700 hover:bs-green-600 text-white font-bold px-3 py-2 rounded-lg disabled:opacity-50"
                  >
                    <Check size={14} /> Deploy
                  </button>
                </div>
              )}
              {templates.length === 0 && (
                <p className="text-gray-600 text-xs">No org templates available. Create one in Org Admin → MCS Builder.</p>
              )}
            </div>
          )}
        </div>
      ) : (
        <div>
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-3">
              <p className="text-xs text-gray-500 uppercase tracking-wide">Command Structure</p>
              <span className="text-xs text-gray-600">{assignedCount}/{nodes.length} staffed</span>
            </div>
            {isGm && (
              <button
                onClick={clearMCS}
                className="text-xs text-gray-500 hover:text-red-400"
              >
                Clear MCS
              </button>
            )}
          </div>

          <div className="bg-gray-800 border border-gray-700 rounded-xl py-2">
            {tree.map(node => renderNode(node, 0))}
          </div>

          <div className="mt-3 flex gap-4 text-xs text-gray-600">
            <span className="flex items-center gap-1"><span className="text-amber-400 font-mono">R</span> Role</span>
            <span className="flex items-center gap-1"><span className="text-blue-400 font-mono">U</span> Unit</span>
            <span className="flex items-center gap-1"><span className="text-green-400 font-mono">E</span> Element</span>
          </div>
        </div>
      )}
    </div>
  )
}
